import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { IconPlus } from '@americanexpress/dls-icons';
import { highlightedAmenityTypes } from '@/constants/highlightedAmenityTypes';
import { centurionAmenityIconMap } from '@/constants/amenityIconMapping';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { resolveImageUrl } from '@/helpers/utils';

const HighlightedAmenities = ({ highlightedAmenities, onOpenModal }) => {
  const oneCmsImagesBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);

  return (
    <ul
      data-qe-id="highlighted-amenities"
      className="pad-0-lr tw-mt-4 tw-flex tw-flex-col tw-gap-4"
    >
      {highlightedAmenityTypes.map(({ id, qeId }) => {
        const highlight = highlightedAmenities?.[id];
        if (!highlight) return null;

        const { title, description, image } = highlight;
        const imageSrc = resolveImageUrl(image, oneCmsImagesBaseUrl);
        if (!title || !description || !imageSrc) return null;

        const AmenityIcon = centurionAmenityIconMap[id];

        return (
          <li
            key={id}
            data-qe-id={`highlighted-amenity-card-${qeId}`}
            className="tw-flex tw-items-center tw-gap-4 tw-self-stretch tw-rounded-2xl tw-bg-gray-200 tw-p-4"
          >
            <span
              data-qe-id={`highlighted-amenity-icon-${qeId}`}
              className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-text-gray-800"
            >
              <AmenityIcon aria-hidden="true" color="inherit" size="md" />
            </span>
            <span className="tw-min-w-0 tw-flex-1">
              <span className="tw-block tw-font-sans tw-text-base tw-font-normal tw-leading-6 tw-text-gray-800">
                {title}
              </span>
              <span className="tw-block tw-font-sans tw-text-xs tw-font-normal tw-leading-normal tw-text-gray-700">
                {description}
              </span>
            </span>
            <button
              type="button"
              data-qe-id={`highlighted-amenity-trigger-${qeId}`}
              className="tw-h-11 tw-min-w-11 tw-shrink-0 tw-cursor-pointer tw-overflow-hidden"
              onClick={(event) => onOpenModal(event, title)}
              aria-label={`Explore ${title}`}
            >
              <span className="tw-relative tw-block tw-h-full tw-w-full tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white">
                <img
                  src={imageSrc}
                  alt=""
                  className="tw-h-full tw-w-full tw-object-cover"
                />
                <span className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-tl-lg tw-border-l tw-border-t tw-border-gray-300 tw-bg-white tw-shadow-sm">
                  <IconPlus
                    aria-hidden="true"
                    size="xs"
                    className="tw-text-gray-700"
                  />
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

HighlightedAmenities.propTypes = {
  highlightedAmenities: PropTypes.shape({
    freshSeasonalFare: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
    }),
    premiumBar: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
    }),
  }),
  onOpenModal: PropTypes.func.isRequired,
};

HighlightedAmenities.defaultProps = {
  highlightedAmenities: {},
};

export default HighlightedAmenities;
